export interface DateTimeParts {
  date: Date | null;
  time: Date | null;
}

// Keep in sync with the backend's accepted formats (ChecklistEndpointExtensions
// IsValidDateTimeInputPayload). They are local wall-clock values with no offset.
const DATE_TIME_VALUE = /^(?:(\d{4})-(\d{2})-(\d{2}))?(?:T?(\d{2}):(\d{2}))?$/;

export function parseDateTimeInputValue(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  return toDateTimeParts(value) ? value : null;
}

export function toDateTimeParts(value: string | null): DateTimeParts | null {
  if (!value) return { date: null, time: null };
  const match = DATE_TIME_VALUE.exec(value);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match;
  const hasDate = y !== undefined;
  const hasTime = h !== undefined;
  if (!hasDate && !hasTime) return null;
  if (value.includes('T') !== (hasDate && hasTime)) return null;

  const date = hasDate ? new Date(+y, +mo - 1, +d) : null;
  if (
    date &&
    (date.getFullYear() !== +y ||
      date.getMonth() !== +mo - 1 ||
      date.getDate() !== +d)
  ) {
    return null;
  }
  if (hasTime && (+h > 23 || +mi > 59)) return null;
  const time = hasTime ? new Date(2000, 0, 1, +h, +mi) : null;
  return { date, time };
}

export function composeDateTimeValue(
  parts: DateTimeParts,
  dateField: boolean,
  timeField: boolean
): string | null {
  const date = dateField ? parts.date : null;
  const time = timeField ? parts.time : null;
  if ((dateField && !date) || (timeField && !time)) return null;

  const datePart = date
    ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    : '';
  const timePart = time
    ? `${pad(time.getHours())}:${pad(time.getMinutes())}`
    : '';
  return datePart && timePart
    ? `${datePart}T${timePart}`
    : datePart || timePart;
}

export function formatDateTimeValue(value: string | null): string {
  return value ? value.replace('T', ' ') : '';
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
